import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { FileText, Download, Calendar, ArrowLeft, ExternalLink, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NumeroDetailPage() {
  const { numero } = useParams();
  const [issue, setIssue] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allIssues, setAllIssues] = useState([]);

  useEffect(() => {
    fetchNumeroDetail();
    fetchAllIssues();
  }, [numero]);

  const fetchNumeroDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/numero/${numero}`);
      if (response.ok) {
        const data = await response.json();
        setIssue(data.issue);
        setArticles(data.articles || []);
      }
    } catch (error) {
      console.error('Error fetching numero:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllIssues = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pdf-issues`);
      const data = await response.json();
      setAllIssues(data.issues || []);
    } catch (error) {
      console.error('Error fetching issues:', error);
    }
  };

  // Find prev/next issues for navigation
  const currentNum = parseInt(numero);
  const prevIssue = allIssues.find(i => i.num_value === currentNum - 1);
  const nextIssue = allIssues.find(i => i.num_value === currentNum + 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h1 className="font-playfair text-2xl font-bold text-stone-900 mb-2">
              Numéro non trouvé
            </h1>
            <p className="text-stone-600 mb-6">
              Le numéro {numero} n'existe pas dans nos archives.
            </p>
            <Link 
              to="/numeros" 
              className="inline-flex items-center gap-2 text-red-700 hover:text-red-800 font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              Voir tous les numéros
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const seoTitle = `Versailles Plus N°${numero} – ${issue.mois || ''} ${issue.annee || ''}`.trim();
  const seoDescription = `Téléchargez le numéro ${numero} de Versailles Plus (${issue.mois || ''} ${issue.annee || ''}). ${articles.length > 0 ? `${articles.length} articles archivés.` : 'Magazine d\'actualités de Versailles.'}`;

  return (
    <div className="min-h-screen bg-stone-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        canonical={`https://versaillesplus.fr/numero/${numero}`}
      />
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm font-inter text-stone-500 mb-6">
          <Link to="/" className="hover:text-red-700">Accueil</Link>
          <span>/</span>
          <Link to="/numeros" className="hover:text-red-700">Numéros</Link>
          <span>/</span>
          <span className="text-stone-900">N°{numero}</span>
        </nav>

        {/* Hero section */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-br from-red-800 to-red-900 p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Cover image or placeholder */}
              <div className="flex-shrink-0">
                {issue.cover_image ? (
                  <img 
                    src={issue.cover_image} 
                    alt={`Couverture VersaillesPlus N°${numero}`}
                    className="w-48 h-64 object-cover rounded-lg shadow-lg"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-48 h-64 bg-white/10 rounded-lg flex items-center justify-center">
                    <FileText className="h-16 w-16 text-white/50" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-3">
                  Versailles Plus N°{numero}
                </h1>
                
                <div className="flex items-center gap-2 text-red-200 mb-6">
                  <Calendar className="h-5 w-5" />
                  <span className="font-inter text-lg">
                    {issue.mois && issue.annee ? `${issue.mois} ${issue.annee}` : `Numéro ${numero}`}
                  </span>
                </div>
                
                <p className="font-lora text-lg text-red-100 mb-6 leading-relaxed max-w-2xl">
                  Magazine mensuel d'actualités versaillaises, fondé en février 2007 par Jean-Baptiste Giraud.
                  Un regard libre et indépendant sur la vie locale.
                </p>
                
                {/* Download button */}
                {issue.pdf_url_local ? (
                  <a 
                    href={`${API_URL}${issue.pdf_url_local}`}
                    download={`versailles-plus-${numero}.pdf`}
                    className="inline-flex items-center gap-3 bg-white text-red-800 px-6 py-3 rounded-lg font-inter font-semibold hover:bg-red-50 transition-colors shadow-lg"
                    data-testid="download-pdf-local"
                  >
                    <Download className="h-5 w-5" />
                    Télécharger le PDF
                  </a>
                ) : issue.pdf_url_original ? (
                  <a 
                    href={issue.pdf_url_original}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-white text-red-800 px-6 py-3 rounded-lg font-inter font-semibold hover:bg-red-50 transition-colors shadow-lg"
                    data-testid="download-pdf-external"
                  >
                    <Download className="h-5 w-5" />
                    Télécharger le PDF
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : (
                  <div className="inline-flex items-center gap-3 bg-white/20 text-white/80 px-6 py-3 rounded-lg font-inter">
                    <FileText className="h-5 w-5" />
                    PDF en cours de numérisation
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation prev/next */}
          <div className="flex justify-between items-center px-6 py-4 bg-stone-50 border-t border-stone-200">
            {prevIssue ? (
              <Link 
                to={`/numero/${prevIssue.num_value}`}
                className="flex items-center gap-2 text-stone-600 hover:text-red-700 font-inter text-sm"
              >
                <ChevronLeft className="h-4 w-4" />
                N°{prevIssue.num_value} {prevIssue.mois && `- ${prevIssue.mois}`}
              </Link>
            ) : <div />}
            
            <Link 
              to="/numeros"
              className="text-stone-600 hover:text-red-700 font-inter text-sm"
            >
              Tous les numéros
            </Link>
            
            {nextIssue ? (
              <Link 
                to={`/numero/${nextIssue.num_value}`}
                className="flex items-center gap-2 text-stone-600 hover:text-red-700 font-inter text-sm"
              >
                N°{nextIssue.num_value} {nextIssue.mois && `- ${nextIssue.mois}`}
                <ChevronRight className="h-4 w-4" />
              </Link>
            ) : <div />}
          </div>
        </div>

        {/* Articles from this issue */}
        {articles.length > 0 && (
          <section className="mb-12">
            <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-6 flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-red-700" />
              Articles de ce numéro ({articles.length})
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link 
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="bg-white rounded-lg border border-stone-200 p-5 hover:border-red-300 hover:shadow-md transition-all"
                >
                  <h3 className="font-playfair text-lg font-semibold text-stone-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  {article.original_author && (
                    <p className="font-inter text-sm text-stone-500 mb-2">
                      Par {article.original_author}
                    </p>
                  )}
                  <p className="font-inter text-sm text-stone-600 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <span className="inline-block mt-3 text-red-700 font-inter text-sm font-medium">
                    Lire l'article →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Info box if no articles */}
        {articles.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-12">
            <h3 className="font-playfair text-lg font-semibold text-amber-900 mb-2">
              Articles en cours d'extraction
            </h3>
            <p className="font-inter text-sm text-amber-800">
              Les articles de ce numéro sont en cours de numérisation et d'indexation. 
              Revenez bientôt pour consulter le contenu détaillé, ou téléchargez directement le PDF ci-dessus.
            </p>
          </div>
        )}

        {/* Metadata for SEO */}
        <section className="bg-white rounded-lg border border-stone-200 p-6">
          <h2 className="font-playfair text-lg font-semibold text-stone-900 mb-4">
            Informations sur ce numéro
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 font-inter text-sm">
            <div>
              <dt className="text-stone-500">Numéro</dt>
              <dd className="text-stone-900 font-medium">N°{numero}</dd>
            </div>
            {issue.mois && (
              <div>
                <dt className="text-stone-500">Mois de parution</dt>
                <dd className="text-stone-900 font-medium">{issue.mois}</dd>
              </div>
            )}
            {issue.annee && (
              <div>
                <dt className="text-stone-500">Année</dt>
                <dd className="text-stone-900 font-medium">{issue.annee}</dd>
              </div>
            )}
            <div>
              <dt className="text-stone-500">Articles extraits</dt>
              <dd className="text-stone-900 font-medium">{articles.length}</dd>
            </div>
            <div>
              <dt className="text-stone-500">PDF disponible</dt>
              <dd className="text-stone-900 font-medium">
                {issue.pdf_url_local ? 'Oui (stockage local)' : issue.pdf_url_original ? 'Oui' : 'En cours'}
              </dd>
            </div>
            <div>
              <dt className="text-stone-500">Éditeur</dt>
              <dd className="text-stone-900 font-medium">Le Pool Presse SAS</dd>
            </div>
          </dl>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
